import React, { useState, useEffect } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Box, InputLabel, Checkbox, ListItemText, ListSubheader } from '@mui/material';

export default function SelectCluster({
  onClusterChange,
  style,
  label,
  defaultCluster = '',
  defaultClusters = [],
  multiSelect = false,
  filterEnv,
  showAll = false
}) {
  let clusters_all = [];
  try {
    clusters_all = JSON.parse(import.meta.env.VITE_CLUSTERS || '[]');
  } catch {
    clusters_all = [];
  }

  // Normalize cluster list
  const normalizedClusters = clusters_all.map((c) => ({
    value: c.value,
    label: c.label ?? c.value,
    env: (c.env || '').toLowerCase(),
  }));

  // Filter environment mapping
  const normalizeFilter = (e) => {
    if (!e) return null;
    const v = e.toString().trim().toLowerCase();
    if (v === 'test') return 'test';
    if (v === 'staging' || v === 'prod' || v === 'production') return 'prod';
    return null;
  };

  const filterKey = normalizeFilter(filterEnv);

  // Filtered list
  const filteredClusters = showAll
    ? normalizedClusters
    : filterKey
    ? normalizedClusters.filter((c) => c.env === filterKey)
    : normalizedClusters;

  // Split into groups
  const testClusters = filteredClusters.filter((c) => c.env === 'test' || c.env === 'dev');
  const prodClusters = filteredClusters.filter((c) => c.env === 'prod');

  // State management
  const [cluster, setCluster] = useState(defaultCluster);
  const [clusters, setClusters] = useState(defaultClusters);

  useEffect(() => {
    if (multiSelect) {
      const valid = clusters.filter((val) =>
        filteredClusters.some((fc) => fc.value === val)
      );
      if (valid.length !== clusters.length) {
        setClusters(valid);
        onClusterChange(valid);
      }
    } else {
      if (cluster && !filteredClusters.some((fc) => fc.value === cluster)) {
        setCluster('');
        onClusterChange('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, showAll]);

  const handleChange = (event) => {
    if (multiSelect) {
      const selected = event.target.value;
      setClusters(selected);
      onClusterChange(selected);
    } else {
      const selected = event.target.value;
      setCluster(selected);
      onClusterChange(selected);
    }
  };

  const renderValue = (selected) => (multiSelect ? selected.join(', ') : selected);

  return (
    <Box sx={{ minWidth: 120, ...style }}>
      <FormControl fullWidth>
        <InputLabel id="cluster-label" size="small">
          {label}
        </InputLabel>
        <Select
          labelId="cluster-label"
          id="cluster"
          size="small"
          multiple={multiSelect}
          value={multiSelect ? clusters : cluster}
          label={label}
          onChange={handleChange}
          renderValue={renderValue}
        >
          {testClusters.length > 0 && <ListSubheader>Test</ListSubheader>}
          {testClusters.map((clusterItem) => (
            <MenuItem key={clusterItem.value} value={clusterItem.value}>
              {multiSelect && (
                <Checkbox checked={clusters.indexOf(clusterItem.value) > -1} />
              )}
              <ListItemText primary={clusterItem.label} />
            </MenuItem>
          ))}

          {prodClusters.length > 0 && <ListSubheader>Staging / Prod</ListSubheader>}
          {prodClusters.map((clusterItem) => (
            <MenuItem key={clusterItem.value} value={clusterItem.value}>
              {multiSelect && (
                <Checkbox checked={clusters.indexOf(clusterItem.value) > -1} />
              )}
              <ListItemText primary={clusterItem.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}